import React from "react";
import { Truck, Gauge, Clock, Globe2, ShieldCheck, CheckCircle, PackageSearch, MoveRight, } from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from 'react-helmet-async';
import processImage from "../assets/images/data/ftl.png";
import MoreServicesSection from "./MoreServicesSection";
// import FooterCTA from "./FooterCTA";
import CTA from "./CTA";

const FTLPage = () => (
  <main className="bg-white text-gray-800 overflow-hidden relative font-sans">
    <Helmet>
      <title>Full Truck Load (FTL) | S INFINITE EXPRESS</title>
      <meta
        name="description"
        content="Get reliable Full Truck Load (FTL) services from S INFINITE EXPRESS. We provide GPS-tracked, secure, and on-time delivery of bulk cargo across India."
      />
      <meta
        name="keywords"
        content="FTL shipping India, Full Truck Load logistics, S INFINITE EXPRESS, Dedicated freight transport, Express logistics, FTL tracking, full load transport service, pan India truck transport, truckload logistics company, long-haul trucking India, GPS-enabled freight, FTL delivery service, full truckload carriers, heavy cargo transport India, secure freight delivery, commercial goods transport, bulk cargo shipping, logistics for manufacturers, FTL for ecommerce, intercity truck transport, freight booking India, reliable trucking company India"
      />
      <meta name="author" content="S Infinite Express" />
      <meta property="og:title" content="Full Truck Load (FTL) | S INFINITE EXPRESS" />
      <meta
        property="og:description"
        content="Book FTL logistics with S INFINITE EXPRESS for bulk cargo delivery across India. Secure, GPS-tracked, and on-time Full Truck Load solutions."
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://sinfiniteexpress.com/services/ftl" />
      <link rel="canonical" href="https://sinfiniteexpress.com/services/ftl" />
    </Helmet>

    {/* INTRO */}
    <section className="max-w-7xl mx-auto py-20 px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center ">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="space-y-6"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-[#211f57]">Why Choose Our FTL?</h2>
        <div className="w-20 h-1 bg-[#d32c30] rounded"></div>

        <p className="text-gray-700 text-lg">
          <span className="font-bold text-[#d32c30]">S INFINITE EXPRESS</span> delivers comprehensive Full Truck Load (FTL) shipping solutions with
          a <span className="text-[#211f57] font-semibold">dedicated fleet</span> of over 300+ GPS-enabled vehicles operating across major routes nationwide.
          From long-haul consignments to time-sensitive express logistics, we ensure your goods are transported safely, securely, and on schedule.
        </p>

        <motion.img
          src={processImage}
          alt="FTL Process Overview"
          className="rounded-3xl shadow-2xl hover:scale-105 transition-transform duration-700 border-4 border-white"
          loading="lazy"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        />
      </motion.div>

      {/* FTL */}
      <div className="bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0] text-black p-8 rounded-2xl shadow-xl space-y-6">

        {/* Heading */}
        <h3 className="text-xl font-bold flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-white/80 flex items-center justify-center shadow">
            <ShieldCheck className="w-5 h-5 text-[#d32c30]" />
          </span>
          Dedicated Full Truckload. Zero Compromise.
        </h3>

        {/* Description */}
        <p className="text-black leading-relaxed">
          Our FTL service ensures <strong>exclusive truck allocation</strong> for your cargo,
          offering faster transit, maximum security, and complete shipment control.
          Ideal for high-volume or time-sensitive freight that demands
          <strong> reliability</strong>, <strong>speed</strong>, and <strong>direct delivery</strong>.
        </p>

        {/* Feature List */}
        <ul className="mt-10 grid sm:grid-cols-2 gap-4 text-sm text-gray-700">
          {[
            "Dedicated truck for your shipment only",
            "Tamper-proof sealed vehicles for enhanced security",
            "Direct point-to-point delivery with no cross-docking",
            "Real-time GPS tracking with proactive alerts",
            "Priority dispatch and faster transit time",
            "Live milestone updates via Email & WhatsApp",
            "Ideal for bulk loads, high-value & urgent shipments",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-bold">
                ✓
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="bg-white/70 p-4 rounded-xl text-center shadow-sm">
            <p className="text-2xl font-extrabold text-[#d32c30]">100%</p>
            <p className="text-gray-800">Dedicated Capacity</p>
          </div>
          <div className="bg-white/70 p-4 rounded-xl text-center shadow-sm">
            <p className="text-2xl font-extrabold text-[#d32c30]">Faster</p>
            <p className="text-gray-800">Direct Transit</p>
          </div>
          <div className="bg-white/70 p-4 rounded-xl text-center shadow-sm">
            <p className="text-2xl font-extrabold text-[#d32c30]">24×7</p>
            <p className="text-gray-800">Live Tracking Support</p>
          </div>
        </div>

        {/* CTA Button */}
        <a
          href="/get-a-quote"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#d32c30] text-white rounded-lg text-sm font-semibold hover:bg-[#b82629] transition shadow-md"
        >
          Book Full Truck
          <MoveRight className="w-4 h-4" />
        </a>
      </div>

    </section>

    {/* Features */}
    <div className="py-20 bg-gradient-to-br from-[#211f57] to-[#2f2c72]">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-extrabold text-center text-[#fff44f] mb-4">
          FTL Services: What We Bring To The Highways
        </h2>
        <p className="text-center text-white max-w-3xl mx-auto mb-12">
          From container trucks to on-time delivery—experience the power of our Full Truck Load logistics.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: <Truck className="w-6 h-6 text-blue-600" />,
              bg: "bg-blue-50",
              title: "Dedicated Fleet of 300++ Trucks",
              description:
                "Fully containerized, weatherproof vehicles for your consignments.",
            },
            {
              icon: <Gauge className="w-6 h-6 text-green-600" />,
              bg: "bg-blue-100",
              title: "Express Logistics",
              description:
                "Optimized routes and express lanes for faster, reliable delivery.",
            },
            {
              icon: <Clock className="w-6 h-6 text-purple-600" />,
              bg: "bg-blue-50",
              title: "Hourly Committed Delivery",
              description:
                "Guaranteed delivery windows to match your schedules.",
            },
            {
              icon: <Globe2 className="w-6 h-6 text-yellow-600" />,
              bg: "bg-blue-100",
              title: "Pan-India Coverage",
              description:
                "Serving industrial hubs and remote areas across the nation.",
            },
            {
              icon: <ShieldCheck className="w-6 h-6 text-orange-600" />,
              bg: "bg-blue-50",
              title: "24/7 GPS Tracking",
              description:
                "Real-time monitoring with instant status updates.",
            },
            {
              icon: <PackageSearch className="w-6 h-6 text-red-600" />,
              bg: "bg-blue-100",
              title: "Secure & Sealed Loads",
              description:
                "Tamper-proof transport with minimal damage risk.",
            },
            {
              icon: <CheckCircle className="w-6 h-6 text-teal-600" />,
              bg: "bg-blue-50",
              title: "COD & DOD Services",
              description:
                "Flexible payment and delivery options for your business.",
            },
            {
              icon: <Gauge className="w-6 h-6 text-pink-600" />,
              bg: "bg-blue-100",
              title: "Optimized Transit Times",
              description:
                "Transit times exceeding industry standards for efficiency.",
            },
            {
              icon: <Truck className="w-6 h-6 text-[#211f57]" />,
              bg: "bg-blue-50",
              title: "Door Pickup & Delivery",
              description:
                "End-to-end solutions from your warehouse to destination.",
            },
          ].map((feature, index) => (
            <div
              key={index}
              className={`group ${feature.bg} p-6 rounded-xl backdrop-blur-sm shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 hover:bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0]`}
            >
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center transition-colors duration-300 group-hover:bg-white">
                  <div className="transition-colors duration-300 group-hover:text-yellow-600">
                    {feature.icon}
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-bold text-[#211f57] group-hover:text-[#211f57] mb-2 group relative inline-block">
                {feature.title}
                <span className="absolute left-0 -bottom-1 h-0.5 w-0 bg-[#211f57] transition-all duration-300 group-hover:w-full"></span>
              </h3>

              <p className="text-sm text-gray-700 group-hover:text-[#211f57] transition-colors duration-300">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>

    <MoreServicesSection />
    {/* <FooterCTA /> */}
    <CTA />
  </main>
);

export default FTLPage;
